import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Container,
  Divider,
  Flex,
  Grid,
  Heading,
  HStack,
  Image,
  Tag,
  TagLabel, Text, useMediaQuery,
  VStack
} from "@chakra-ui/react";
import { AvatarCard } from "@components/Cards/AvataraCard/AvatarCard";
import { MetaCard } from "@components/Cards/MetaCard/MetaCard";
import { HomeIcon } from "@components/Icons/HomeIcon";
import { WidgetTitle } from "@components/Texts/WidgetTitle/WidgetTitle";
import { Tile, TILE_MODES } from "@components/Tiles/Tile";
import { generateBlogCategoryUrl, generateBlogTagUrl } from "@core/paths";
import { preparePostUrl } from "@core/utils";
import { Link as GatsbyLink, Link } from "@reach/router";
import { Disqus } from 'gatsby-plugin-disqus';
import { hyphenateHTMLSync } from "hyphen/pl";
import * as React from "react";
import { CarouselProvider } from "../../components/Carousel/Carousel";
import { PageCategory, Post } from "../../core/types";
import { htmldecode } from "../../utils/htmlDecode";
import "./scss/index.scss";

interface PageProps {
  // todo update type
  page: Post;
  categories: PageCategory[];
  suggestedPosts: PageCategory[];
  setLoading?: (loading: boolean) => void;
  nextPost?: any
}

export const Page: React.FC<PageProps> = ({
  page,
  suggestedPosts,
  nextPost
}) => {
  const [isDesktop] = useMediaQuery("(min-width: 992px)");
  const { configPosts, title, excerpt, content, featuredImage, categories, tags, id } = page;
  const firstCategory = categories?.nodes ? categories.nodes[0] : null;

  return (
    <>
      <Container m={{ base: "0 auto 2rem", sm: "50px auto 2rem" }} maxW="710px">
        <Box mb="43px" as="header">
          {/* <Button className="article-page__return" onClick={() => navigate(-1)}>
          &#x3031; Powrót
          </Button> */}
          {firstCategory && (
            <HStack spacing="15px" mb="20px" mx="-1rem" px="1rem" py=".7rem" alignItems="center" bg={{ base: '#D1E6FF', lg: 'transparent' }}>
              <HomeIcon />
              <Breadcrumb fontSize="12px" pt="3px" separator={isDesktop ? '/' : <ChevronRightIcon color="gray.500" />}>
                {firstCategory.parent?.node && (
                  <BreadcrumbItem>
                    <BreadcrumbLink as={Link} to={generateBlogCategoryUrl(firstCategory.parent.node.slug)} color="#686D88">
                      {firstCategory.parent.node.name}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                )}

                <BreadcrumbItem isCurrentPage>
                  <BreadcrumbLink as={Link} to={generateBlogCategoryUrl(firstCategory.slug, firstCategory.parent?.node?.slug)} color="#686D88">
                    {firstCategory.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </Breadcrumb>
            </HStack>
          )}
          {!isDesktop && (<MetaCard article={page} mb="10px" />)}
          <Heading fontSize={{ base: "22px", sm: "46px" }} fontWeight="700" mb="20px">
            {title}
          </Heading>
          {excerpt.indexOf('Continue reading') === -1 && (
            <Box
              mb="38px"
              color="#9B9B9B"
              fontSize={{ base: "16px", sm: "18px" }}
              dangerouslySetInnerHTML={{
                __html: hyphenateHTMLSync(htmldecode(excerpt)),
              }}
            />
          )}
          <Flex justifyContent="space-between" mb="30px">
            <AvatarCard author={page.author} />
            {isDesktop && (<MetaCard article={page} />)}
          </Flex>

          <Image
            mb="43px"
            src={featuredImage?.node?.blogPost}
            className="article__header-image"
          />
          <Box
            className="editor-styles-wrapper"
            fontSize={{ base: "16px", sm: "21px" }}
            color="#303030"
            fontFamily="Mulish"
            lineHeight={{ base: "1.5", sm: "1.3" }}
            sx={{
              a: {
                textDecoration: "underline",
              },
            }}
            dangerouslySetInnerHTML={{
              __html: hyphenateHTMLSync(htmldecode(content)),
            }}
          />

          {configPosts?.lastUpdate && (
            <Box fontStyle="italic" mb="20px">Ostatnia aktualizacja: {configPosts.lastUpdate}</Box>
          )}

          {tags?.nodes && (
            <Box mb="20px">
              {tags.nodes.map(({ id, name, slug }) => (
                <Tag key={id} as={Link} to={generateBlogTagUrl(slug)} size="sm" borderRadius="none" variant="solid" color="#71869E" cursor="pointer"
                  backgroundColor="#E7F2FF" px="8px" py="6px" mr="5px" mb="5px">
                  <TagLabel textTransform="uppercase" fontSize="12px">{name}</TagLabel>
                </Tag>
              ))}
            </Box>
          )}

          <Flex justifyContent="space-between">
            {isDesktop && (<AvatarCard author={page.author} />)}
            <MetaCard article={page} />
          </Flex>

          {
            typeof window !== "undefined" &&
            window.location.pathname.includes('trudne-pytania') &&
            !!nextPost &&
            nextPost.categories?.nodes?.filter((el: any) => el.slug === 'trudne-pytania').length > 0 &&
            (
              <Flex mt="15px" justifyContent="flex-end"  >
                <Text p="5px 10px" cursor="pointer" to={preparePostUrl(nextPost)} borderRadius="5px" as={GatsbyLink} color="black" overflow="hidden" background="rgb(234,244,255)">
                  Następne trudne pytanie
                </Text>
              </Flex>
            )
          }

        </Box>

        {/*
        <MediaQuery minWidth={1024}>
          <div>
            <Link to="/">
              <button className="article-page__all-posts">
                powrót do wszystkich artykułów
              </button>
            </Link>
          </div>
        </MediaQuery>
        */}
        <Disqus
          config={{
            url: `https://mamaginekolog.pl${preparePostUrl(page)}`,
            identifier: `${id}`,
            title: `${title}`,
          }}
        />
      </Container>

      <Container maxW="1130px" px="15px">
        <Divider bg="#DDECFB" h="2px" mb="40px" />

        <VStack alignItems="flex-start" spacing="30px">
          <WidgetTitle color="#222C63">ZOBACZ RÓWNIEŻ</WidgetTitle>

          <Box w="100%" d={{ base: "block", sm: "none" }}>
            <CarouselProvider>
              {suggestedPosts.map(post => (<Tile key={post.id} mode={TILE_MODES.SHORT_WITH_THUMBNAIL} article={post} />))}
            </CarouselProvider>
          </Box>

          <Grid d={{ base: "none", sm: "grid" }} templateColumns="repeat(4, 1fr)" gap="32px">
            {suggestedPosts.map(post => (<Tile key={post.id} mode={TILE_MODES.SHORT_WITH_THUMBNAIL} article={post} />))}
          </Grid>
        </VStack>
      </Container>
    </>
  );
};

export default Page;
